import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../models/artifact';
import { ItineraryStep } from '../models/itinerary';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DeveloperArtifactAssemblyPickerComponent } from '../developer-artifacts/developer-artifact-assembly-picker.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-itinerary-steps',
    templateUrl: './developer-itinerary-steps.component.html',
    styleUrls: ['./developer-itinerary-steps.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DeveloperArtifactAssemblyPickerComponent]
})
export class DeveloperItineraryStepsComponent implements OnInit {

    hasItinStepCreatePerm = false;
    hasItinStepEditPerm = false;
    hasItinStepDeletePerm = false;
    hasItinStepShowLogPerm = false;

    itinerarySteps: ItineraryStep[] = [];
    artifacts: AssemblyArtifact[] = [];

    constructor(
        private developerService: DeveloperService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private loadingPanelService: LoadingPanelService,
        private developerArtifactService: DeveloperArtifactService,
        public bizGridFiltervalues: BizGridFiltervalues,
        private gridService: GridService) {

        this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
        this.allowDeletingRow = this.allowDeletingRow.bind(this);
        this.allowShowLog = this.allowShowLog.bind(this);
        this.displayExprArtifact = this.displayExprArtifact.bind(this);
    }

    async ngOnInit() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeItineraryStep, ArtifactAssemblyTypes.TypeItineraryStep2]);
        this.hasItinStepCreatePerm = this.currentUserService.hasPermission('Developer_Action_ItineraryStep_Create');
        this.hasItinStepEditPerm = this.currentUserService.hasPermission('Developer_Action_ItineraryStep_Edit');
        this.hasItinStepDeletePerm = this.currentUserService.hasPermission('Developer_Action_ItineraryStep_Delete');
        this.hasItinStepShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog');
        this.refresh();
    }

    allowUpdatingRow(options: { component: any; row: any }): boolean {
        const item: ItineraryStep = options?.row?.data;
        if (item) {
            return this.hasItinStepEditPerm && !item.IsSystemItineraryStep;
        }
        return false;
    }

    allowDeletingRow(options: { component: any; row: any }): boolean {
        const item: ItineraryStep = options?.row?.data;
        if (item) {
            return this.hasItinStepDeletePerm && !item.IsSystemItineraryStep;
        }
        return false;
    }

    allowShowLog(options: { component: any; row: any }): boolean {
        const item: ItineraryStep = options?.row?.data;
        if (item) {
            return this.hasItinStepShowLogPerm && !item.IsSystemItineraryStep;
        }
        return false;
    }

    displayExprArtifact(e: any) {
        // TODO: Find en bedre måde at håndtere dette, så det ikke er nødvendigt at loade alle artifacts...
        if (this.artifacts) {
            const artifact = this.artifacts.find(a => a.ArtifactId === e.value);
            if (artifact) {
                return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
            }
        }
        return 'Unknown...';
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading itinerary steps...');
        this.itinerarySteps = await this.developerService.getItinerarySteps();
        this.loadingPanelService.hideLoadingPanel();
    }

    onInitNewRow(e: any): void {
        e.data = { Id: null, Name: '', Description: '', IsSystemItineraryStep: false } as ItineraryStep;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as ItineraryStep, item => this.saveItinearyStep(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as ItineraryStep, item => this.saveItinearyStep(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as ItineraryStep, item => this.removeItinearyStep(item));
    };

    async saveItinearyStep(itineraryStep: ItineraryStep): Promise<ItineraryStep> {
        const itineraryStepExist = this.itinerarySteps.find(i => i.StepKey === itineraryStep.StepKey && i.Id !== itineraryStep.Id);
        if (itineraryStepExist != null) {
            throw new Error(`Another itinerary step with that StepKey already exists. Name of the existing step: ${itineraryStepExist.Name}`);
        }

        const newStep = await this.developerService.saveItineraryStep(itineraryStep);
        this.toastService.showToast('Itinerary step saved...', ToastType.success);
        return newStep;
    }

    async removeItinearyStep(itineraryStep: ItineraryStep): Promise<ItineraryStep> {
        await this.developerService.deleteItineraryStep(itineraryStep.Id);
        this.toastService.showToast('Itinerary step deleted...', ToastType.success);
        return itineraryStep;
    }

    async setArtifactId(artifactId: string, cellInfo: any) {
        cellInfo.setValue(artifactId || '');
        if (cellInfo.data.ArtifactId !== artifactId && artifactId) { // Only check artifact if it has changed!
            try {
                this.loadingPanelService.showLoadingPanelDirect('Verifying artifact...');
                const itineraryStepKey = artifactId ? await this.developerService.getItineraryStepKey(artifactId) : null;
                //cellInfo.data.StepKey = itineraryStepKey;
                cellInfo.component.cellValue(cellInfo.row.rowIndex, 'StepKey', itineraryStepKey);
            } finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
    }

    customizeItem(item: any) {
        if (item.dataField === 'IsSystemItineraryStep') { // Hide the 'Download' column when editing...
            item.visible = false;
        }
    }

    onEditorPreparing(e: any) {
        if (e.dataField === 'Description') {
            //e.editorName = "dxTextArea"; <-- Not working anymore
        }
    }
}
